import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { MapPinIcon, WifiIcon } from '@heroicons/react/24/outline';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { normalizeLocationArray, prioritizeLocations } from '../utils/locationUtils';

export interface LocationRemotenessSectionProps {
  location?: string | string[];
  remoteness?: string | null;
}

export const LocationRemotenessSection: React.FC<LocationRemotenessSectionProps> = ({
  location,
  remoteness,
}) => {
  const intl = useIntl();

  const locationsArray = normalizeLocationArray(location);
  const { primaryLocations, subtitleLocations } = prioritizeLocations(locationsArray);

  return (
    <div className="mb-6">
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Location */}
        <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 shadow-md">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
              <MapPinIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1 uppercase tracking-wide">
                <FormattedMessage 
                  id="jobDetailPage.location" 
                  defaultMessage="Location" 
                  description="Section label for job location information" 
                />
              </div>
              {primaryLocations.length === 0 ? (
                <span className="text-blue-600 dark:text-blue-400 italic">
                  <FormattedMessage 
                    id="jobDetailPage.noLocation" 
                    defaultMessage="No location" 
                    description="Fallback text when job location is not available" 
                  />
                </span>
              ) : (
                <>
                  {primaryLocations.length > 0 && (
                    <div className="text-lg font-bold text-blue-900 dark:text-blue-100">
                      {primaryLocations.map((loc, idx) => (
                        <span key={loc + idx}>
                          {fixMojibake(loc)}
                          {idx < primaryLocations.length - 1 && ', '}
                        </span>
                      ))}
                    </div>
                  )}
                  {subtitleLocations.length > 0 && (
                    <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      {subtitleLocations.map((loc, idx) => (
                        <span key={loc + idx}>
                          {fixMojibake(loc)}
                          {idx < subtitleLocations.length - 1 && ', '}
                        </span>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Remoteness */}
        <div className="p-4 rounded-2xl bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-200 dark:border-purple-800 shadow-md">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
              <WifiIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-1 uppercase tracking-wide">
                <FormattedMessage 
                  id="jobDetailPage.remoteness" 
                  defaultMessage="Remoteness" 
                  description="Section label for work model/remoteness information (remote, hybrid, onsite)" 
                />
              </div>
              <div className="text-lg font-bold text-purple-900 dark:text-purple-100 truncate">
                {remoteness ? (
                  fixMojibake(remoteness)
                ) : (
                  <span className="text-purple-600 dark:text-purple-400 italic">
                    <FormattedMessage 
                      id="jobDetailPage.noRemoteness" 
                      defaultMessage="Not specified" 
                      description="Fallback text when work model/remoteness is not specified" 
                    />
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
